import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { NavLink, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { deletePlan, getPlansByMissionId } from '../../redux/plans/planActions'
import { AppDispatch, RootState, useAppDispatch } from '../../redux/store'
import { formatTitle } from '../../utils/formatTitle'
import { Plan } from '../../types/planTypes'
import {
  DetailEntityPanel,
  DetailTable,
} from '../../components/common/detail'
import {
  canManageMission,
  DetailRowActions,
} from '../../components/common/detail/DetailRowActions'
import {
  ListPageDateCell,
  ListPagePill,
  MATURITY_PILL,
  STATUS_PILL,
} from '../../components/common/ListPageCells'
import { ListPageDescription } from '../../components/common/ListPageDescription'
import { Language, maturiteLabels } from '../../utils/constants'
import '../../components/common/ListPageTable.css'
import i18n from '../../i18n'

type Props = {
  IdMission: string
  onCountChange?: (count: number) => void
}

const PAGE_SIZE = 100

function maturityLabel(value: string | undefined, lang: string): string {
  if (!value) return '—'
  const fromMap = maturiteLabels[value]
  if (fromMap) {
    return fromMap[lang as Language] || fromMap.en
  }
  return value
}

const PlanListByMission: React.FC<Props> = ({ IdMission, onCountChange }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch: AppDispatch = useAppDispatch()
  const roles = useSelector((state: any) => state.auth.roles)
  const loading = useSelector((state: RootState) => state.plan.loading)
  const response = useSelector((state: RootState) => state.plan.PlanResponse)
  const [search, setSearch] = useState('')
  const locale = i18n.language

  useEffect(() => {
    if (IdMission) {
      dispatch(
        getPlansByMissionId({
          MissionId: IdMission,
          page: 1,
          pageSize: PAGE_SIZE,
        })
      )
    }
  }, [dispatch, IdMission])

  const rows = useMemo(() => {
    const list = response?.plans ?? []
    const q = search.trim().toLowerCase()
    if (!q) return list
    return list.filter((p) => (p.PlanName || '').toLowerCase().includes(q))
  }, [response?.plans, search])

  useEffect(() => {
    onCountChange?.(response?.total ?? rows.length)
  }, [onCountChange, response?.total, rows.length])

  const canAdd = canManageMission(roles)

  return (
    <DetailEntityPanel
      listTitle={t('missionDetailsPage.sections.planList', {
        defaultValue: 'Risk treatment plans',
      })}
      searchPlaceholder={t('filterByName', { defaultValue: 'Filter by name' })}
      searchValue={search}
      onSearchChange={setSearch}
      addLabel={t('buttons.addPlan', {
        defaultValue: 'Add New Risk Treatment Plan',
      })}
      onAdd={() => navigate(`/mission/${IdMission}/addplan`)}
      canAdd={canAdd}
    >
      <DetailTable<Plan>
        loading={loading && !response}
        loadingLabel={t('loading')}
        emptyTitle={t('missionDetailsPage.empty.title', {
          defaultValue: 'Nothing here yet',
        })}
        emptyMessage={t('missionDetailsPage.empty.plans', {
          defaultValue:
            'No risk treatment plan is linked to this mission yet.',
        })}
        rows={rows}
        rowKey={(r) => r._id as string}
        columns={[
          {
            key: 'name',
            header: t('Plan Name', { defaultValue: 'Plan name' }),
            render: (r) => (
              <NavLink
                to={`/plan-detail/${r._id}`}
                className="incyia-name-link"
              >
                <ListPageDescription
                  value={r.PlanName ? formatTitle(r.PlanName) : undefined}
                  tone="primary"
                />
              </NavLink>
            ),
          },
          {
            key: 'maturity',
            header: t('Maturity Objective', {
              defaultValue: 'Maturity objective',
            }),
            render: (r) => {
              const matKey = String(r.ObjectifMaturite || '')
              const style = MATURITY_PILL[matKey] ?? {
                bg: '#EAF0F0',
                color: '#5F7484',
              }
              return (
                <ListPagePill
                  label={maturityLabel(matKey, locale)}
                  bg={style.bg}
                  color={style.color}
                />
              )
            },
          },
          {
            key: 'status',
            header: t('Status'),
            render: (r) => {
              const style = STATUS_PILL[r.Status || ''] ?? {
                bg: '#EAF0F0',
                color: '#5F7484',
              }
              return (
                <ListPagePill
                  label={t(r.Status || '', {
                    defaultValue: r.Status || '—',
                  })}
                  bg={style.bg}
                  color={style.color}
                />
              )
            },
          },
          {
            key: 'end',
            header: t('End Date', { defaultValue: 'End date' }),
            render: (r) => (
              <ListPageDateCell value={r.DateFin} locale={locale} />
            ),
          },
        ]}
        actionsHeader={t('Actions', { defaultValue: 'Action' })}
        renderActions={(r) => (
          <DetailRowActions
            roles={roles}
            onEdit={() => navigate(`/plan/${r._id}`)}
            onDelete={() => dispatch(deletePlan(r._id as string))}
            deleteItem={{
              entityType: t('plans', { defaultValue: 'Plan' }),
              name: r.PlanName ? formatTitle(r.PlanName) : undefined,
              details: [
                {
                  label: t('deleteConfirm.fields.mission'),
                  value: r.MissionName
                    ? formatTitle(r.MissionName)
                    : '—',
                },
                {
                  label: t('deleteConfirm.fields.status'),
                  value: r.Status || '—',
                },
              ],
            }}
          />
        )}
      />
    </DetailEntityPanel>
  )
}

export default PlanListByMission
